import {
    DatafeedConfiguration,
    ErrorCallback,
    GetMarksCallback,
    HistoryCallback,
    HistoryDepth,
    IDatafeedChartApi as DatafeedChartApi,
    IDatafeedQuotesApi as DatafeedQuotesApi,
    IExternalDatafeed as ExternalDatafeed,
    LibrarySymbolInfo,
    Mark,
    OnReadyCallback,
    QuotesCallback,
    ResolutionBackValues,
    ResolutionString,
    ResolveCallback,
    SearchSymbolsCallback,
    ServerTimeCallback,
    SubscribeBarsCallback,
    TimescaleMark
} from '../charting-library/datafeed-api';

import {DataPulseProvider} from './data-pulse-provider';
import {DataPulseProviderThrottled} from './data-pulse-provider-throttled';

import {getErrorMessage, logMessage, RequestParams, UdfErrorResponse} from './helpers';

import {GetBarsResult, HistoryProvider} from './history-provider';
import {QuotesProvider} from './quotes-provider';
import {QuotesPulseProvider} from './quotes-pulse-provider';
import {Requester} from './requester';
import {SymbolsStorage} from './symbols-storage';

export interface UdfCompatibleConfiguration extends DatafeedConfiguration {
    supports_search?: boolean;
    supports_group_request?: boolean;
}

const DEFAULT_SEARCH_ITEMS_LIMIT = 30;
const DEFAULT_UPDATE_FREQUENCY = 10 * 1000;
const DEFAULT_SUPPORTED_RESOLUTIONS = ['1', '5', '15', '30', '60', '1D', '1W', '1M'];

type UdfDatafeedMarkType<T extends TimescaleMark | Mark> = {
    [K in keyof T]: T[K] | Array<T[K]>;
} & {
    id: Array<string | number>;
};

type UdfDatafeedMark = UdfDatafeedMarkType<Mark>;
type UdfDatafeedTimescaleMark = UdfDatafeedMarkType<TimescaleMark>;

function extractField<Field extends keyof Mark>(
    data: UdfDatafeedMark, field: Field, arrayIndex: number): Mark[Field];
function extractField<Field extends keyof TimescaleMark>(
    data: UdfDatafeedTimescaleMark,
    field: Field, arrayIndex: number): TimescaleMark[Field];
function extractField<Field extends keyof (TimescaleMark & Mark)>(
    data: UdfDatafeedMark | UdfDatafeedTimescaleMark,
    field: Field, arrayIndex: number): (TimescaleMark & Mark)[Field] {
    const value = data[field as keyof (UdfDatafeedMark | UdfDatafeedTimescaleMark)];
    return (Array.isArray(value) ? value[arrayIndex] : value) as any;
}

/**
 * This class implements interaction with UDF-compatible datafeed.
 * See UDF protocol reference at https://github.com/tradingview/charting_library/wiki/UDF
 */
export class UDFCompatibleDatafeedBase implements ExternalDatafeed, DatafeedQuotesApi, DatafeedChartApi {
    protected configuration: UdfCompatibleConfiguration = defaultConfiguration();
    private readonly datafeedURL: string;
    private readonly configurationReadyPromise: Promise<void>;

    private symbolsStorage: SymbolsStorage | null = null;

    private readonly historyProvider: HistoryProvider;
    private readonly dataPulseProvider: DataPulseProvider;

    private readonly quotesProvider: QuotesProvider;
    private readonly quotesPulseProvider: QuotesPulseProvider;

    private readonly requester: Requester;

    protected constructor(datafeedURL: string,
                          quotesProvider: QuotesProvider,
                          requester: Requester,
                          historyProvider: HistoryProvider,
                          updateFrequency: number = DEFAULT_UPDATE_FREQUENCY) {
        this.datafeedURL = datafeedURL;
        this.requester = requester;
        this.historyProvider = historyProvider;
        this.quotesProvider = quotesProvider;

        this.dataPulseProvider = new DataPulseProviderThrottled(this.historyProvider, updateFrequency);
        this.quotesPulseProvider = new QuotesPulseProvider(this.quotesProvider);

        this.configurationReadyPromise = this.requestConfiguration()
            .then((configuration: UdfCompatibleConfiguration | null) => {
                if (configuration === null) {
                    configuration = defaultConfiguration();
                }

                this.setupWithConfiguration(configuration);
            });
    }

    public onReady(callback: OnReadyCallback): void {
        this.configurationReadyPromise.then(() => {
            callback(this.configuration);
        });
    }

    public getQuotes(symbols: string[], onDataCallback: QuotesCallback, onErrorCallback: (msg: string) => void): void {
        this.quotesProvider.getQuotes(symbols).then(onDataCallback).catch(onErrorCallback);
    }

    public subscribeQuotes(symbols: string[],
                           fastSymbols: string[],
                           onRealtimeCallback: QuotesCallback,
                           listenerGuid: string): void {
        this.quotesPulseProvider.subscribeQuotes(symbols, fastSymbols, onRealtimeCallback, listenerGuid);
    }

    public unsubscribeQuotes(listenerGuid: string): void {
        this.quotesPulseProvider.unsubscribeQuotes(listenerGuid);
    }

    public calculateHistoryDepth(resolution: ResolutionString,
                                 resolutionBack: ResolutionBackValues,
                                 intervalBack: number): HistoryDepth | undefined {
        return undefined;
    }

    public getMarks(symbolInfo: LibrarySymbolInfo,
                    startDate: number,
                    endDate: number,
                    onDataCallback: GetMarksCallback<Mark>,
                    resolution: ResolutionString): void {
        if (!this.configuration.supports_marks) {
            return;
        }

        const requestParams: RequestParams = {
            symbol: symbolInfo.ticker || '',
            from: startDate,
            to: endDate,
            resolution
        };

        this.send<Mark[] | UdfDatafeedMark>('marks', requestParams)
            .then(response => {
                if (!Array.isArray(response)) {
                    const result: Mark[] = [];
                    for (let i = 0; i < response.id.length; ++i) {
                        result.push({
                            id: extractField(response, 'id', i),
                            time: extractField(response, 'time', i),
                            color: extractField(response, 'color', i),
                            text: extractField(response, 'text', i),
                            label: extractField(response, 'label', i),
                            labelFontColor: extractField(response, 'labelFontColor', i),
                            minSize: extractField(response, 'minSize', i)
                        });
                    }

                    response = result;
                }

                onDataCallback(response);
            })
            .catch((error?: string | Error) => {
                logMessage(`UdfCompatibleDatafeed: Request marks failed: ${getErrorMessage(error)}`);
                onDataCallback([]);
            });
    }

    public getTimescaleMarks(symbolInfo: LibrarySymbolInfo,
                             startDate: number,
                             endDate: number,
                             onDataCallback: GetMarksCallback<TimescaleMark>,
                             resolution: ResolutionString): void {
        if (!this.configuration.supports_timescale_marks) {
            return;
        }

        const requestParams: RequestParams = {
            symbol: symbolInfo.ticker || '',
            from: startDate,
            to: endDate,
            resolution
        };

        this.send<TimescaleMark[] | UdfDatafeedTimescaleMark>('timescale_marks', requestParams)
            .then(response => {
                if (!Array.isArray(response)) {
                    const result: TimescaleMark[] = [];
                    for (let i = 0; i < response.id.length; ++i) {
                        result.push({
                            id: extractField(response, 'id', i),
                            time: extractField(response, 'time', i),
                            color: extractField(response, 'color', i),
                            label: extractField(response, 'label', i),
                            tooltip: extractField(response, 'tooltip', i)
                        });
                    }

                    response = result;
                }

                onDataCallback(response);
            })
            .catch((error?: string | Error) => {
                logMessage(`UdfCompatibleDatafeed: Request timescale marks failed: ${getErrorMessage(error)}`);
                onDataCallback([]);
            });
    }

    public getServerTime(callback: ServerTimeCallback): void {
        if (!this.configuration.supports_time) {
            return;
        }

        this.send<string>('time')
            .then(response => {
                const time = parseInt(response, undefined);
                if (!isNaN(time)) {
                    callback(time);
                }
            })
            .catch((error?: string | Error) => {
                logMessage(`UdfCompatibleDatafeed: Fail to load server time, error=${getErrorMessage(error)}`);
            });
    }

    public searchSymbols(userInput: string,
                         exchange: string,
                         symbolType: string,
                         onResult: SearchSymbolsCallback): void {
        if (this.configuration.supports_search) {
            const params: RequestParams = {
                limit: DEFAULT_SEARCH_ITEMS_LIMIT,
                query: userInput.toUpperCase(),
                type: symbolType,
                exchange
            };

            this.send('search', params)
                .then((response: any) => {
                    if (response.s !== undefined) {
                        const message = `UdfCompatibleDatafeed: search symbols error=`;
                        logMessage(`${message}${(response as UdfErrorResponse).errmsg}`);
                        onResult([]);
                        return;
                    }

                    onResult(response);
                })
                .catch((reason?: string | Error) => {
                    const message = `UdfCompatibleDatafeed: Search symbols for `;
                    logMessage(`${message}'${userInput}' failed. Error=${getErrorMessage(reason)}`);
                    onResult([]);
                });
        } else {
            if (this.symbolsStorage === null) {
                throw new Error('UdfCompatibleDatafeed: inconsistent configuration (symbols storage)');
            }

            this.symbolsStorage.searchSymbols(userInput, exchange, symbolType, DEFAULT_SEARCH_ITEMS_LIMIT)
                .then(onResult)
                .catch(onResult.bind(null, []));
        }
    }

    public resolveSymbol(symbolName: string, onResolve: ResolveCallback, onError: ErrorCallback): void {
        logMessage('Resolve requested');

        const resolveRequestStartTime = Date.now();

        function onResultReady(symbolInfo: LibrarySymbolInfo): void {
            logMessage(`Symbol resolved: ${Date.now() - resolveRequestStartTime}ms`);
            onResolve(symbolInfo);
        }

        if (!this.configuration.supports_group_request) {
            const params: RequestParams = {
                symbol: symbolName
            };

            this.send('symbols', params)
                .then((response: any) => {
                    if (response.s !== undefined) {
                        onError('unknown_symbol');
                    } else {
                        onResultReady(response);
                    }
                })
                .catch((reason?: string | Error) => {
                    logMessage(`UdfCompatibleDatafeed: Error resolving symbol: ${getErrorMessage(reason)}`);
                    onError('unknown_symbol');
                });
        } else {
            if (this.symbolsStorage === null) {
                throw new Error('UdfCompatibleDatafeed: inconsistent configuration (symbols storage)');
            }

            this.symbolsStorage.resolveSymbol(symbolName).then(onResultReady).catch(onError);
        }
    }

    public getBars(symbolInfo: LibrarySymbolInfo,
                   resolution: ResolutionString,
                   rangeStartDate: number,
                   rangeEndDate: number,
                   onResult: HistoryCallback,
                   onError: ErrorCallback): void {
        this.historyProvider.getBars(symbolInfo, resolution, rangeStartDate, rangeEndDate)
            .then((result: GetBarsResult) => {
                onResult(result.bars, result.meta);
            })
            .catch(onError);
    }

    public subscribeBars(symbolInfo: LibrarySymbolInfo,
                         resolution: ResolutionString,
                         onTick: SubscribeBarsCallback,
                         listenerGuid: string,
                         onResetCacheNeededCallback: () => void): void {
        this.dataPulseProvider.subscribeBars(symbolInfo, resolution, onTick, listenerGuid);
    }

    public unsubscribeBars(listenerGuid: string): void {
        this.dataPulseProvider.unsubscribeBars(listenerGuid);
    }

    protected requestConfiguration(): Promise<UdfCompatibleConfiguration | null> {
        return this.send<UdfCompatibleConfiguration>('config')
            .catch((reason?: string | Error): null => {
                const message = `UdfCompatibleDatafeed: Cannot get datafeed configuration - use default, error=`;
                logMessage(`${message}${getErrorMessage(reason)}`);
                return null;
            });
    }

    private send<T>(urlPath: string, params?: RequestParams): Promise<T> {
        return this.requester.sendRequest<T>(this.datafeedURL, urlPath, params);
    }

    private setupWithConfiguration(configurationData: UdfCompatibleConfiguration): void {
        this.configuration = configurationData;

        if (configurationData.exchanges === undefined) {
            configurationData.exchanges = [];
        }

        if (!configurationData.supports_search && !configurationData.supports_group_request) {
            const message = 'Unsupported datafeed configuration. Must either support search, or support group request';
            throw new Error(message);
        }

        if (configurationData.supports_group_request || !configurationData.supports_search) {
            this.symbolsStorage = new SymbolsStorage(
                this.datafeedURL,
                configurationData.supported_resolutions || [],
                this.requester);
        }

        logMessage(`UdfCompatibleDatafeed: Initialized with ${JSON.stringify(configurationData)}`);
    }
}

function defaultConfiguration(): UdfCompatibleConfiguration {
    return {
        supports_search: false,
        supports_group_request: true,
        supported_resolutions: DEFAULT_SUPPORTED_RESOLUTIONS as ResolutionString[],
        supports_marks: false,
        supports_timescale_marks: false
    };
}
